import { formatUsStateName } from "@wags/utils/us-states.ts";

export type ProviderLocationInput = {
    city: string | null;
    state: string | null;
    isPrimary: boolean;
};

export type ProviderPlace = {
    city: string;
    state: string;
    label: string;
};

export type ProviderLocationSummary = {
    primary: ProviderPlace | null;
    others: ProviderPlace[];
    stateCount: number;
};

export type ProviderStateGroup = {
    state: string | null;
    cities: string[];
};

export function stateDisplayName(state: string): string {
    return formatUsStateName(state) ?? state;
}

function placeName(place: ProviderPlace): string {
    return place.city || stateDisplayName(place.state);
}

function toPlace({ city, state }: ProviderLocationInput): ProviderPlace {
    return {
        city: city ?? "",
        state: state ?? "",
        label: [city, state].filter(Boolean).join(", "),
    };
}

export function summarizeProviderLocations(
    locations: readonly ProviderLocationInput[],
): ProviderLocationSummary | null {
    if (locations.length === 0) return null;
    const primary = locations.find((location) => location.isPrimary);
    return {
        primary: primary ? toPlace(primary) : null,
        others: locations
            .filter((location) => location !== primary)
            .map(toPlace),
        stateCount: new Set(
            locations.flatMap((location) =>
                location.state ? [location.state] : [],
            ),
        ).size,
    };
}

export function countPlaces({ primary, others }: ProviderLocationSummary) {
    return others.length + (primary ? 1 : 0);
}

export function leadPlace({
    primary,
    others,
}: ProviderLocationSummary): ProviderPlace | undefined {
    return primary ?? others[0];
}

const ALSO_IN_LIST = new Intl.ListFormat("en", { type: "conjunction" });

function sameLabel(a: string, b: string) {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function formatAlsoIn(
    summary: ProviderLocationSummary,
    shownLabel: string,
): string | null {
    const places = summary.primary
        ? [summary.primary, ...summary.others]
        : summary.others;
    const isShown = (place: ProviderPlace) =>
        place === summary.primary || sameLabel(place.label, shownLabel);
    const rest = places.filter((place) => !isShown(place));
    if (rest.length === 0) return null;
    const shown = rest.slice(0, 3);
    const nameCounts = new Map<string, number>();
    for (const place of [...places.filter(isShown), ...shown]) {
        const key = placeName(place).toLowerCase();
        nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    }
    const names = shown.map((place) =>
        (nameCounts.get(placeName(place).toLowerCase()) ?? 0) > 1
            ? place.label
            : placeName(place),
    );
    const remaining = rest.length - names.length;
    const list = ALSO_IN_LIST.format(
        remaining > 0 ? [...names, `${remaining} more`] : names,
    );
    const across =
        summary.stateCount >= 2 ? ` across ${summary.stateCount} states` : "";
    return `Also in ${list}${across}`;
}

export function formatLocationsCaption(
    summary: ProviderLocationSummary,
): string {
    const count = countPlaces(summary);
    const { stateCount } = summary;
    if (stateCount === 0) return `${count} locations`;
    return `${count} across ${stateCount} ${stateCount === 1 ? "state" : "states"}`;
}

export function groupPlacesByState(
    places: readonly ProviderPlace[],
): ProviderStateGroup[] {
    const groups = new Map<string | null, string[]>();
    for (const place of places) {
        const state = place.state || null;
        const cities = groups.get(state) ?? [];
        cities.push(place.city);
        groups.set(state, cities);
    }
    return [...groups]
        .map(([state, cities]) => ({ state, cities }))
        .sort((a, b) => {
            if (a.state === null) return 1;
            if (b.state === null) return -1;
            return stateDisplayName(a.state).localeCompare(
                stateDisplayName(b.state),
            );
        });
}
